import { TappFormField } from '@/components/ui/TappFormField';
import { TappInput } from '@/components/ui/TappInput';
import { TappLabel } from '@/components/ui/TappLabel';
import { TappPageHeader } from '@/components/ui/TappPageHeader';
import { Button } from '@/components/ui/button';
import { cn } from '@/components/ui/utils';
import { Company, CreateCompanyDto } from '@/lib/models/company';
import { ApiUtils } from '@/lib/utils/api';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { useAlert } from '@/components/ui/alert-dialog-provider';

const CompanyCreatePage = () => {
  const { t } = useTranslation();

  return (
    <>
      <TappPageHeader title={t('addCompany')} defaultBack backLink="/admin/companies" />
      <CreateForm />
    </>
  );
};

const CreateForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const alert = useAlert();

  const companySchema = z.object({
    name: z.string().min(1, t('name') + ' ' + t('isRequired')),
    subaccountId: z.string().optional(),
    dayTariff: z
      .number({
        required_error: t('dayTariff') + ' ' + t('isRequired'),
        invalid_type_error: t('dayTariff') + ' ' + t('isRequired'),
      })
      .positive(t('dayTariff') + ' ' + t('mustBePositive'))
      .refine((val) => Number(val.toFixed(2)) === val, {
        message: t('dayTariff') + ' ' + t('maxTwoDecimals'),
      }),
    nightTariff: z
      .number({
        required_error: t('nightTariff') + ' ' + t('isRequired'),
        invalid_type_error: t('nightTariff') + ' ' + t('isRequired'),
      })
      .positive(t('nightTariff') + ' ' + t('mustBePositive'))
      .refine((val) => Number(val.toFixed(2)) === val, {
        message: t('nightTariff') + ' ' + t('maxTwoDecimals'),
      }),
  });

  const {
    handleSubmit,
    control,
    formState: { isSubmitting },
  } = useForm<CreateCompanyDto>({
    resolver: zodResolver(companySchema),
    defaultValues: {
      name: '',
      subaccountId: '',
      dayTariff: undefined,
      nightTariff: undefined,
    },
  });

  const mutation = useMutation({
    mutationFn: (data: CreateCompanyDto) => ApiUtils.post<Company>('company', data),
    onSuccess: async () => {
      await alert({
        title: t('success'),
        body: t('companyCreated'),
      });
      navigate('/admin/companies');
    },
    onError: async (err) => {
      console.error(err);
      await alert({
        title: t('error'),
        body: ApiUtils.convertApiErrorToString(err),
      });
    },
  });

  const onSubmit = (data: CreateCompanyDto) => {
    mutation.mutate(data);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className={cn('flex w-full max-w-md flex-1 flex-col items-start gap-2')}>
      <TappFormField>
        <TappLabel htmlFor="name">{t('name')}</TappLabel>
        <Controller
          name="name"
          control={control}
          render={({ field, fieldState }) => (
            <TappInput className="w-full" placeholder={t('companyNamePlaceholder')} {...field} {...fieldState} />
          )}
        />
      </TappFormField>

      <TappFormField>
        <TappLabel htmlFor="subaccountId">{t('subaccountId')}</TappLabel>
        <Controller
          name="subaccountId"
          control={control}
          render={({ field, fieldState }) => (
            <TappInput className="w-full" placeholder={t('subaccountIdPlaceholder')} {...field} {...fieldState} />
          )}
        />
      </TappFormField>

      <TappFormField>
        <TappLabel htmlFor="dayTariff">{t('dayTariff')}</TappLabel>
        <Controller
          name="dayTariff"
          control={control}
          render={({ field, fieldState }) => (
            <TappInput
              className="w-full"
              type="number"
              step="0.01"
              min="0.01"
              placeholder={t('dayTariffPlaceholder')}
              {...field}
              {...fieldState}
              onChange={(e) => field.onChange(e.target.value ? parseFloat(e.target.value) : undefined)}
            />
          )}
        />
      </TappFormField>

      <TappFormField>
        <TappLabel htmlFor="nightTariff">{t('nightTariff')}</TappLabel>
        <Controller
          name="nightTariff"
          control={control}
          render={({ field, fieldState }) => (
            <TappInput
              className="w-full"
              type="number"
              step="0.01"
              min="0.01"
              placeholder={t('nightTariffPlaceholder')}
              {...field}
              {...fieldState}
              onChange={(e) => field.onChange(e.target.value ? parseFloat(e.target.value) : undefined)}
            />
          )}
        />
      </TappFormField>

      <Button className="mt-2" type="submit" disabled={isSubmitting || mutation.isPending}>
        {mutation.isPending ? t('saving') : t('save')}
      </Button>
    </form>
  );
};

export default CompanyCreatePage;
